/**
 * Ultra Simple Jackpot Bottom Band
 *
 * Just reads the current animated value from the centralized store.
 * No animation logic, no seeding, no complexity.
 * Perfect synchronization because everyone reads from the same source.
 */

import React, { useRef, useEffect, useLayoutEffect, memo, useCallback } from 'react';
import type IGameSettings from '../../../../../../types/models/IGameSettings';
import { useFormattedJackpotValue } from '../../../../../jackpots/store/jackpotsStoreSimple';
import { useAppStore } from 'store/app/appStore';
import styles from './JackpotBottomBand.module.css';
import { getNoJackpotException, getJackpotSlug } from '../../utils/jackpotBandLabel';
import { observeJackpotBand, scheduleJackpotBandFit, unobserveJackpotBand } from './fitJackpotBand';

interface JackpotBottomBandProps {
  data: IGameSettings;
  currency: string;
  denomination: number;
  sectionName?: string;
  sectionGames?: unknown[];
}
const JackpotBottomBandSimple: React.FC<JackpotBottomBandProps> = memo(({
  data,
  currency,
}) => {
  const numberRef = useRef<HTMLSpanElement>(null);
  const currencyRef = useRef<HTMLSpanElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { viewMode } = useAppStore.getState();

  // Get formatted value from centralized store (GRAND level for game cards)
  const grandLevelId = 1;
  const formattedValue = useFormattedJackpotValue(data.jackpotName || '', grandLevelId);

  const noJackpotExceptionLabel = getNoJackpotException(data.gameFeatures);
  const displayText = noJackpotExceptionLabel ?? formattedValue;
  // Starts at the mounted text's length: the first fit comes from the resize observer (see below)
  const lastValueLengthRef = useRef<number>(displayText.length);

  const getBandElements = useCallback(() => {
    const container = containerRef.current;
    const numberSpan = numberRef.current;
    const currencySpan = currencyRef.current;

    if (!container || !numberSpan || !currencySpan) return null;
    return { container, numberSpan, currencySpan };
  }, []);

  // Simple font resize function
  const updateFontSize = useCallback(() => {
    scheduleJackpotBandFit(getBandElements);
  }, [getBandElements]);

  // Update display when value changes
  useEffect(() => {
    if (numberRef.current) {
      // Only recalculate font size when string length changes (value crossed a digit boundary)
      if (displayText.length !== lastValueLengthRef.current) {
        lastValueLengthRef.current = displayText.length;
        updateFontSize();
      }
    }
  }, [displayText, updateFontSize]);

  // Handle container resize. Observed from a layout effect so the observer's first callback - which also does the
  // initial fit - runs in this frame, after layout and before paint: it reads sizes the browser has just computed
  // instead of forcing a layout from an effect (one per band while a row of cards mounts during fast scroll).
  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    observeJackpotBand(container, getBandElements);

    return () => {
      unobserveJackpotBand(container);
    };
  }, [getBandElements]);

  if (!data.jackpotName && !noJackpotExceptionLabel) {
    return null;
  }

  const normalizedJackpotName = getJackpotSlug(noJackpotExceptionLabel ?? data.jackpotName);

  return (
    <div data-view={viewMode} data-jackpot={normalizedJackpotName} className={styles.jackpotBand}>
      <div ref={containerRef} className={`${styles.valueContainer} value_container`}>
        <div className={`${styles.contentWrapper} content_wrapper`}>
          <span
            data-view={viewMode}
            data-jackpot={normalizedJackpotName}
            ref={numberRef}
            className={`${styles.dynamicText} dynamic_text`}
          >
            {displayText}
          </span>
          {!noJackpotExceptionLabel && (
            <span ref={currencyRef} className={`${styles.currency} currency`}>
              {currency}
            </span>
          )}
        </div>
      </div>
    </div>
  );
});

export default JackpotBottomBandSimple;
